import { h } from 'preact';
import { Fragment } from 'preact/compat';
import { useTranslation } from 'react-i18next';
import { withTheme } from 'styled-components';
import { themePropTypes } from 'proptypes/theme.proptypes';
import { ReactComponent as PerformAiIcon } from 'icons/performai-logo.svg';
import { ReactComponent as YoutubeIcon } from 'icons/youtube.svg';
import { ReactComponent as LinkedInIcon } from 'icons/linkedin.svg';
import { ReactComponent as FacebookIcon } from 'icons/fb.svg';
import { ReactComponent as TwitterIcon } from 'icons/twitter.svg';
import { ReactComponent as InstagramIcon } from 'icons/instagram.svg';
import {
  Container,
  LinksContainer,
  PoweredByContainer,
  PoweredBy,
  LogoWrapper,
  TermAndCond,
  Icons,
  ContactSupportIconWrapper,
} from './Footer.styles';
import { DEFAULT_FOOTER_DESTINATION_URL } from './Footer.constants';
import Link from '../Link';
import { ReactComponent as ContactSupportIcon } from '../../icons/contact.svg';

const Footer = ({ theme }) => {
  const { t } = useTranslation();
  const { footerBox, marketingCollateralBox } = theme.configs;
  const {
    destinationURL,
    termConditionsURL,
    privacyPolicyURL,
    shouldShowPPLogo = true,
    termConditionsText,
    privacyPolicyText,
    socialMedia,
  } = footerBox;

  const hasNoMarketingCollateralBox =
    !marketingCollateralBox ||
    marketingCollateralBox.length === 0 ||
    marketingCollateralBox.every((item) => !item.bgImage);

  const hasLinkBox =
    socialMedia.some((media) => media.enable) ||
    termConditionsURL ||
    privacyPolicyURL;

  return (
    <Container>
      <PoweredByContainer>
        {shouldShowPPLogo && (
          <Fragment>
            <PoweredBy>{t('POWERED_BY')}</PoweredBy>
            <Link
              url={destinationURL || DEFAULT_FOOTER_DESTINATION_URL}
              ariaLabel={t('ALT_TEXT_FOOTER_PERFORM_AI_LINK')}
            >
              <LogoWrapper>
                <PerformAiIcon data-testid="performai-icon" aria-hidden />
              </LogoWrapper>
            </Link>
          </Fragment>
        )}
      </PoweredByContainer>
      {hasLinkBox && (
        <LinksContainer>
          <Icons>
            {socialMedia.map((item) => {
              const socialMediaName = item.name === 'Twitter' ? 'X' : item.name;
              const ariaLabel = t('ALT_TEXT_FOOTER_SOCIAL_MEDIA_LINK', {
                socialMediaName,
              });

              return (
                item.enable && (
                  <Link url={item.url} key={item.name} ariaLabel={ariaLabel}>
                    {item.name === 'Youtube' && (
                      <YoutubeIcon data-testid="youtube" />
                    )}
                    {item.name === 'Linkedin' && (
                      <LinkedInIcon data-testid="linkedin" />
                    )}
                    {item.name === 'Facebook' && (
                      <FacebookIcon data-testid="facebook" />
                    )}
                    {item.name === 'Instagram' && (
                      <InstagramIcon data-testid="instagram" />
                    )}
                    {item.name === 'Twitter' && (
                      <TwitterIcon data-testid="twitter" />
                    )}
                  </Link>
                )
              );
            })}
          </Icons>
          <div>
            {termConditionsURL && (
              <Link url={termConditionsURL}>
                <TermAndCond>
                  {termConditionsText || t('TERMS_AND_CONDITIONS')}
                </TermAndCond>
              </Link>
            )}
            {privacyPolicyURL && (
              <Link url={privacyPolicyURL}>
                {privacyPolicyText || t('PRIVACY_HEADER')}
              </Link>
            )}
          </div>
        </LinksContainer>
      )}

      {hasNoMarketingCollateralBox && !!footerBox.customerServiceURL && (
        <ContactSupportIconWrapper
          url={footerBox.customerServiceURL}
          ariaLabel={t('ALT_TEXT_FOOTER_CUSTOMER_SERVICE_LINK')}
        >
          <ContactSupportIcon />
        </ContactSupportIconWrapper>
      )}
    </Container>
  );
};

Footer.propTypes = {
  theme: themePropTypes.isRequired,
};

export default withTheme(Footer);
